import Modal from './styles';

import Animation from 'services/utils/Animation';

import MobileHeader from '../MobileHeader';
import Navigation from '../Navigation';

/**
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useCallback é um hook que retorna uma função de retorno
 * de chamada memorizada
 * 
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import Mobile from 'views/MapView/components/Mobile';

/**
 * TabModal
 * 
 * @param {string} title
 * @param {array} open
 * @param {function} onClose
 * @param {array} views
 * 
 * @returns 
 */
const TabModal = ({ title, open, onClose, views, sectionWidth, sectionHeight }) => {
  const [activeView, setActiveView] = useState(0);
  const flex = useRef();

  const moveTo = useCallback(
    (index) => (e) => {
      setActiveView(index);
    },
    [setActiveView]
  );

  /**
   * Ação de animação até o step ativo activeView
   */
  const scrollStep = useCallback(() => {
    if (flex.current) {
      let scrollLeft = flex.current.scrollLeft;
      let width = flex.current.offsetWidth;
      const animation = new Animation(
        500,
        (stepNumber) => {
          flex.current.scrollLeft =
            scrollLeft * (1 - stepNumber) + activeView * width * stepNumber;
        },
        () => {
          flex.current.scrollLeft = activeView * width;
        }
      );
      animation.start();
    }
  }, [activeView, flex]);

  // Escuta as alterações em activeView para fazer andar as tabs
  useEffect(() => {
    if (typeof activeView !== 'undefined') {
      scrollStep();
    }
  }, [activeView, scrollStep]);

  //Reseta o activeView para a view inicial ao fechar o modal
  useEffect(() => {
    if (!open) {
      setActiveView(0);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sectionWidth={sectionWidth}
      sectionHeight={sectionHeight}
    >
      <section>
        <button type="button" onClick={onClose}>
          <svg
            width="18"
            height="17"
            viewBox="0 0 18 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line
              y1="-0.75"
              x2="12"
              y2="-0.75"
              transform="matrix(-0.707107 0.707107 0.707107 0.707107 13.7141 4.82849)"
              stroke="#5E6366"
              strokeWidth="1.5"
            />
            <line
              y1="-0.75"
              x2="12"
              y2="-0.75"
              transform="matrix(0.707107 0.707107 0.707107 -0.707107 5.22852 3.71497)"
              stroke="#5E6366"
              strokeWidth="1.5"
            />
          </svg>
        </button>
        <header>
          <Mobile>
            <MobileHeader title={title} onClick={onClose} />
          </Mobile>
          <Navigation views={views} activeView={activeView} moveTo={moveTo} />
        </header>
        <div className="scrollable" ref={flex}>
          {views.map(({ Component }) => Component)}
        </div>
      </section>
    </Modal>
  );
};

TabModal.properties = {
  ignore: true,
};

export default TabModal;
